import { Stack } from '@mui/material';
import { useLayoutEffect, useMemo } from 'react';

import { CompoundHeader } from '@/components/display/CompoundHeader/CompoundHeader';
import { useDPFormSuggestionsContext } from '@/modules/aiDP/context/dpFormSuggestions.context';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';

import { useDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import { DispositionSchemeSelector } from '../../DispositionSchemeSelector/DispositionSchemeSelector';
import { DispositionScheme } from '../../dispositiveProvisions.types';
import {
  useActiveClientProfile,
  useDispositionScheme,
  useHasBeenReviewed,
  useOrderedDyingClients,
} from '../hooks/utilityHooks';

interface InitialDispositionSchemeSelectorProps {
  onSchemeSelection: (scheme: DispositionScheme) => void;
}

export function InitialDispositionSchemeSelector({
  onSchemeSelection,
}: InitialDispositionSchemeSelectorProps) {
  const canEditHouseholdData = useCanEditDispositiveProvisions();
  const {
    client: activeClientProfile,
    isFirstToDie: activeClientProfileIsFirstToDie,
  } = useActiveClientProfile();

  const { isTwoClientHousehold, isClientProfile } =
    useDispositiveProvisionsContext();

  const [firstClientToDie] = useOrderedDyingClients();

  const { hasSuggestions } = useDPFormSuggestionsContext();

  const hasBeenReviewed = useHasBeenReviewed();
  const dispositionScheme = useDispositionScheme();

  const subheading = useMemo(() => {
    if (isTwoClientHousehold && !isClientProfile) {
      return `If ${firstClientToDie.displayGivenName} dies first`;
    }

    if (!isTwoClientHousehold) return undefined;

    // the last case here is that we're a two-client household looking at a client profile
    // configuration situation
    if (activeClientProfileIsFirstToDie) {
      return `If ${activeClientProfile?.displayGivenName} dies first`;
    }

    return `If ${firstClientToDie.displayGivenName} dies first and ${activeClientProfile?.displayGivenName} dies second`;
  }, [
    activeClientProfile?.displayGivenName,
    activeClientProfileIsFirstToDie,
    firstClientToDie.displayGivenName,
    isClientProfile,
    isTwoClientHousehold,
  ]);

  useLayoutEffect(() => {
    // automatically select the UPON_FIRST_DEATH scheme for single-client households.
    if (
      !hasBeenReviewed &&
      dispositionScheme === DispositionScheme.NONE &&
      hasSuggestions &&
      !isTwoClientHousehold
    ) {
      onSchemeSelection(DispositionScheme.UPON_FIRST_DEATH);
    }
  }, [
    hasBeenReviewed,
    dispositionScheme,
    onSchemeSelection,
    hasSuggestions,
    isTwoClientHousehold,
  ]);

  return (
    <Stack spacing={2}>
      <CompoundHeader
        heading="When do dispositions occur?"
        subheading={subheading}
      />
      <DispositionSchemeSelector
        onChange={onSchemeSelection}
        disabled={!canEditHouseholdData}
      />
    </Stack>
  );
}
